// S-04 Protection form model (Frontend §7.2; Security §3.8; API.md §7.15): what the form edits (the S-04 fields), its
// client-side checks (the same bounds as the server's), the overrides it sends, and the change list the confirm
// dialog shows.
import type { ClinicSecurity, ProtectionOverridesUpdate } from '@pa/api/admin'
import { LIMIT_LABELS, limitLabel, MODE_LABELS } from './labels'
import { MAX_ORIGINS, ORIGIN_MESSAGES, originProblems } from './origins'

/** Security §3.8 bounds (D-57). */
export const CAP_COUNT = { min: 1, max: 20 } as const
export const CAP_DAYS = { min: 7, max: 30 } as const
/** Each limit's hard value may be at most this many times its default (Security §3.8). */
export const LIMIT_FACTOR = 10

/** The thresholds table's rows: every limit code the API returns, in `LIMIT_LABELS` order first. */
export function limitCodes(defaults: ClinicSecurity): string[] {
  const known = Object.keys(LIMIT_LABELS).filter((c) => c in defaults.limits)
  return [...known, ...Object.keys(defaults.limits).filter((c) => !known.includes(c))]
}

export interface LimitValues {
  soft: string
  hard: string
}

export interface ProtectionFormValues {
  capCount: string
  capDays: string
  botMode: 'monitor' | 'enforce'
  limits: Record<string, LimitValues>
  origins: { value: string }[]
}

const text = (n: number | undefined) => (n === undefined ? '' : String(n))

/** The form's values from a `ClinicSecurity` (effective, or the defaults for "Reset to defaults"). */
export function formValuesOf(s: ClinicSecurity, codes: readonly string[]): ProtectionFormValues {
  return {
    capCount: String(s.bookingCap.count),
    capDays: String(s.bookingCap.days),
    botMode: s.scoreChallengeMode,
    limits: Object.fromEntries(codes.map((c) => [c, { soft: text(s.limits[c]?.soft), hard: text(s.limits[c]?.hard) }])),
    origins: s.allowedEmbedOrigins.map((value) => ({ value })),
  }
}

const num = (v: string): number | undefined => {
  const t = v.trim()
  return /^\d{1,9}$/.test(t) ? Number(t) : undefined
}

/** A field path → its message (react-hook-form paths: `capCount`, `limits.ip_lookups.hard`, `origins.2.value`). */
export type FormErrors = Record<string, string>

/** Client-side checks (Security §3.8): the server refuses the same values. */
export function validateForm(v: ProtectionFormValues, defaults: ClinicSecurity): FormErrors {
  const errors: FormErrors = {}
  const count = num(v.capCount)
  if (count === undefined || count < CAP_COUNT.min || count > CAP_COUNT.max) {
    errors.capCount = `Enter ${String(CAP_COUNT.min)}–${String(CAP_COUNT.max)}.`
  }
  const days = num(v.capDays)
  if (days === undefined || days < CAP_DAYS.min || days > CAP_DAYS.max) {
    errors.capDays = `Enter ${String(CAP_DAYS.min)}–${String(CAP_DAYS.max)} days.`
  }
  for (const [code, values] of Object.entries(v.limits)) {
    const d = defaults.limits[code] ?? {}
    const soft = d.soft === undefined ? undefined : num(values.soft)
    const hard = d.hard === undefined ? undefined : num(values.hard)
    if (d.soft !== undefined && (soft === undefined || soft < 1)) {
      errors[`limits.${code}.soft`] = 'Enter a whole number of at least 1.'
    }
    if (d.hard !== undefined) {
      const max = d.hard * LIMIT_FACTOR
      if (hard === undefined || hard < 1) errors[`limits.${code}.hard`] = 'Enter a whole number of at least 1.'
      else if (hard > max) errors[`limits.${code}.hard`] = `At most ${String(max)} (10 × the default).`
      else if (soft !== undefined && hard <= soft) errors[`limits.${code}.hard`] = 'Must be more than the soft limit.'
    }
  }
  if (v.origins.length > MAX_ORIGINS) errors.origins = `At most ${String(MAX_ORIGINS)} websites.`
  for (const [i, problem] of originProblems(v.origins.map((o) => o.value))) {
    errors[`origins.${String(i)}.value`] = ORIGIN_MESSAGES[problem]
  }
  return errors
}

/**
 * The S-04 overrides to send (API.md §7.15): every S-04 field, with the values as the form has them (the server
 * drops the ones equal to the defaults). A limit's window and block length aren't edited here: their current
 * values go back unchanged, so a value set with `security:set` is kept.
 */
export function overridesOf(v: ProtectionFormValues, effective: ClinicSecurity): ProtectionOverridesUpdate {
  const limits: NonNullable<ProtectionOverridesUpdate['limits']> = {}
  for (const [code, values] of Object.entries(v.limits)) {
    const e = effective.limits[code] ?? {}
    const soft = num(values.soft)
    const hard = num(values.hard)
    limits[code] = {
      ...(soft !== undefined && { soft }),
      ...(hard !== undefined && { hard }),
      ...(e.windowSec !== undefined && { windowSec: e.windowSec }),
      ...(e.blockSec !== undefined && { blockSec: e.blockSec }),
    }
  }
  return {
    bookingCap: { count: num(v.capCount) ?? 0, days: num(v.capDays) ?? 0 },
    scoreChallengeMode: v.botMode,
    limits,
    allowedEmbedOrigins: v.origins.map((o) => o.value.trim()).filter((o) => o !== ''),
  }
}

const cap = (count: string | number, days: string | number) => `${String(count)} per ${String(days)} days`

/** The confirm dialog's list: one line per change, before → after (Frontend §7.2 "summarizes the changes"). */
export function changesOf(before: ClinicSecurity, v: ProtectionFormValues): string[] {
  const out: string[] = []
  if (String(before.bookingCap.count) !== v.capCount.trim() || String(before.bookingCap.days) !== v.capDays.trim()) {
    out.push(
      `Booking cap: ${cap(before.bookingCap.count, before.bookingCap.days)} → ${cap(v.capCount.trim(), v.capDays.trim())}`,
    )
  }
  if (before.scoreChallengeMode !== v.botMode) {
    out.push(`Bot mode: ${MODE_LABELS[before.scoreChallengeMode]} → ${MODE_LABELS[v.botMode]}`)
  }
  for (const [code, values] of Object.entries(v.limits)) {
    const b = before.limits[code] ?? {}
    for (const field of ['soft', 'hard'] as const) {
      const was = b[field] === undefined ? '' : String(b[field])
      const now = values[field].trim()
      if (was !== now) out.push(`${limitLabel(code)} (${field}): ${was || '—'} → ${now || '—'}`)
    }
  }
  const after = v.origins.map((o) => o.value.trim()).filter((o) => o !== '')
  for (const o of after.filter((x) => !before.allowedEmbedOrigins.includes(x)))
    out.push(`Allowed embed origin added: ${o}`)
  for (const o of before.allowedEmbedOrigins.filter((x) => !after.includes(x))) {
    out.push(`Allowed embed origin removed: ${o}`)
  }
  return out
}
